import { Injectable, OnDestroy } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { BehaviorSubject, Subject } from 'rxjs';
import { RemoteControlSessionService } from './session.service';
import { McRcEventsService, AgentRecordingStopReason, SotiAssistStatus } from '../../remote-control-ng';
import { BroadcastService, BroadcastChannels } from 'app/utils/broadcastService';
import { RemoteControlService } from './remote-control.service';
import { DeviceStateRegistryService, RecordingState, RemoteControlDeviceState } from './device-state-registry.service';
import { LicenceInfoService, hd } from '@nx-fe/services';
import { RcContext } from '../../remote-control-ng/context/rcContext'
import { AgentRecordingGetMessage, AgentRecordingMessage, AgentRecordingPauseMessage, AgentRecordingResumeMessage, AgentRecordingStartMessage, AgentRecordingStopMessage } from '../../remote-control-ng/messages/pocket-com';
import { VideoRecordingPauseMessage, VideoRecordingStartMessage, VideoRecordingStopMessage } from '../../remote-control-ng/messages/xsight';
import { XsI18nPipe } from '@nx-fe/pipes';
import { ActivityTypeEnum, AttachmentActivity, FileInfo } from 'app/modules/ticket/ticket.model';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { AttachmentService } from 'app/modules/ticket/services/attachment.service';
import { IFileInfo } from '../file-manager/fileData';
import { PromptDialogComponent, FileSaveParams } from 'app/shared/components/prompt-dialog/prompt-dialog.component';

// Represents simplified, current status of video recording
export interface VideoRecordingStatus {
    // Current recording state
    state: RecordingState;
    // Whether running recording with audio
    withAudio: boolean;
    // Assist Server status
    serverStatusCode?: SotiAssistStatus;
    // Win32 error fromm PocketCOMM message header
    win32ErrorCode?: number;
    // Error reason from video recording with audio (agent recording stop message)
    // Indicates specific error when video recording is stopped
    // on the agent side (usually due to reaching size/time limits)
    recordingWithAudioStopReason?: AgentRecordingStopReason;
}

/**
 * Handles video recording functionality
 */
@Injectable()
export class RemoteControlVideoRecordingService implements OnDestroy {

    // Interval of a timer (in ms)
    private readonly TIMER_INTERVAL = 1000;
    public readonly recordingWithAudioAvailableAgentVersion = '14.0.0';
    public readonly recordingWithAudioAvailableOsVersion = '5.1.0';

    // Emits recording status updates with errors
    // Can be used the corresponding UI to display the status and errors
    // Stores latest status to be emitted to all future subscribers
    private readonly videoRecordingStatusChanged: BroadcastService<VideoRecordingStatus> = new BroadcastService(BroadcastChannels.onVideoRecordingStatusChanged);

    public get recodingState(): RecordingState {

        return this.rcState
            ? this.rcState.recordingState
            : RecordingState.NotRunning;
    }

    public get isRecordingPendingAccept(): boolean {
        return (this.rcState &&
            this.rcState.recordingState === RecordingState.PendingAccept);
    }

    public get isRecordingRunning(): boolean {
        return this.rcState && this.rcState.recording;
    }

    public get isVideoRecordingEnabled() {
        return !this.rcService.isStandalone;
    }

    public get isVideoRecordingWithAudioSupported(): boolean {
        const isCompatibleAgentVersion = this.rcService.compareAgentVersion(this.recordingWithAudioAvailableAgentVersion) >= 0;
        const iscompatibleOs = this.rcService.compareOsVersion(this.recordingWithAudioAvailableOsVersion) >= 0;
        return this.rcService.isAndroidDevice && !this.rcService.isStandalone && isCompatibleAgentVersion && iscompatibleOs;
    }

    public get isVideoRecordingWithAudioEnabled(): boolean {
        return this.isVideoRecordingWithAudioSupported && this.licenseService.isLicenseActive();
    }

    private get rcState(): RemoteControlDeviceState {
        return this.rcService.rcState;
    }

    public get isVideoWithAudio() {
        return this.rcState.recordingWithAudio;
    };

    // Allows to pause/resume currently running timer (emit true/false correspondingly)
    public pause = new BehaviorSubject(false);

    private onDestroy = new Subject<void>();

    constructor(
        private rcService: RemoteControlSessionService,
        private rcEventsService: McRcEventsService,
        private licenseService: LicenceInfoService,
        private legacyRcService: RemoteControlService,
        private deviceStateRegistry: DeviceStateRegistryService,
        private saI18nPipe: XsI18nPipe,
        private attachmentService: AttachmentService,
        private dialog: MatDialog,
    ) {
        rcEventsService.rcVideoRecordingStarted
            .pipe(takeUntil(this.onDestroy))
            .subscribe(this.onVideoRecordingStarted);

        rcEventsService.rcVideoRecordingStopped
            .pipe(takeUntil(this.onDestroy))
            .subscribe(this.onVideoRecordingStopped);

        rcEventsService.rcAgentRecordingStopped
            .pipe(takeUntil(this.onDestroy))
            .subscribe(this.onAgentVideoRecordingStopped);

        rcEventsService.rcAgentRecordingStoppedByUser
            .pipe(takeUntil(this.onDestroy))
            .subscribe(this.onAgentVideoRecordingStoppedByUser);

        rcEventsService.rcServerStatusUpdate
            .pipe(takeUntil(this.onDestroy))
            .subscribe(this.onServerStatusUpdate);

        // Because Angular 1 uses polling to check if video is attached to an activity
        // and there is no COMM message for this notfication, for now we have to inject
        // legacy service to get this notification from the polling mechanism
        legacyRcService.setNewVideoAvailableCallback(this.onServerVideoRecordingNewVideoAvailable);
        this.videoRecordingStatusChanged.onMessage = this.onVideoRecordingStatusChanged;
    }

    public ngOnDestroy(): void {
        this.onDestroy.next();
        this.onDestroy.complete();

        if (this.videoRecordingStatusChanged) {
            this.videoRecordingStatusChanged.close();
        }
    }

    // This is invoked by server to indicate server-side
    // video recording(without audio) has been started
    public onVideoRecordingStarted = (event: any) => {
        this.transitionToRunning(false);
    };

    public onVideoRecordingStatusChanged = (event: any) => {
        if (event.state == RecordingState.NotRunning) {
            this.stopAndResetTimer();
        }
    };

    // This will get invoked whenever server intends to stop recording
    // due to server-related event/error.
    // IMPORTANT: Currently it is not invoked when browser client
    // requests to stop the recording
    public onVideoRecordingStopped = (event: any) => {
        this.transitionToStopped();
    };

    // Triggered by the agent when recording limits are reached
    public onAgentVideoRecordingStopped = (event: any) => {
        this.transitionToUploading(undefined, event.errorReason);
        RcContext.instance.session.sendRequest(new AgentRecordingGetMessage())
            .pipe(takeUntil(this.onDestroy))
            .subscribe((message: AgentRecordingMessage) => {
                // This will emit error on uploading
                this.transitionToStopped(message.response.errorCode);
            });
    };

    // Triggered by the agent when recording limits are reached
    public onAgentVideoRecordingStoppedByUser = (event: any) => {
        if (!event.errorCode) {
            // This method does need to be called. Otherwise the server wont send the vide activity
            // But we dont need to change the video recording state state here,
            // as it will be done when the activity gets attached to the ticket
            RcContext.instance.session.sendCallbackRequest(new AgentRecordingGetMessage(), (response) => { });
        } else {
            // Upload will not happen, transition to stopped
            this.transitionToStopped(event.errorCode);
        }
    };

    public pauseVideoRecording() {
        if (!this.rcState.recordingPaused && this.isRecordingRunning) {
            if (this.rcState.recordingWithAudio) {
                RcContext.instance.session.sendMessage(new AgentRecordingPauseMessage());
            } else {
                RcContext.instance.session.sendMessage(new VideoRecordingPauseMessage());
            }
            this.transitionToPaused();
        } // else we have serious error as recording is already paused
    }

    public resumeVideoRecording() {
        if (this.rcState.recordingPaused) {
            if (this.rcState.recordingWithAudio) {
                RcContext.instance.session.sendMessage(new AgentRecordingResumeMessage());
            } else {
                RcContext.instance.session.sendMessage(new VideoRecordingPauseMessage());
            }
            this.transitionToRunning(this.rcState.recordingWithAudio);
        } // else we have serious error as recording is not paused
    }

    public startVideoRecording(withAudio: boolean = false) {
        const self = this;
        if (!this.isRecordingRunning) {
            if (withAudio && this.isVideoRecordingWithAudioEnabled) {
                this.transitionToPendingAccept();
                RcContext.instance.session.sendRequest(new AgentRecordingStartMessage({
                    screenWidth: this.rcState.agentVideoRecordingPrefs.screenWidth,
                    screenHeight: this.rcState.agentVideoRecordingPrefs.screenHeight,
                    videoFrameRate: this.rcState.agentVideoRecordingPrefs.videoFrameRate,
                    videoBitRate: this.rcState.agentVideoRecordingPrefs.videoBitRate,
                    audioSamplingRate: this.rcState.agentVideoRecordingPrefs.audioSamplingRate,
                    audioBitRate: this.rcState.agentVideoRecordingPrefs.audioBitRate
                }))
                    .pipe(takeUntil(this.onDestroy))
                    .subscribe((message: AgentRecordingMessage) => {
                        if (!message.response.errorCode) {
                            self.transitionToRunning(true);
                        } else {
                            self.transitionToStopped(message.response.errorCode);
                        }
                    });
            } else if (this.isVideoRecordingEnabled) {
                RcContext.instance.session.sendMessage(new VideoRecordingStartMessage(this.rcState.videoRecordingPrefs));
            }
            if (!this.deviceStateRegistry.getStopRecordingCallback()) {
                this.deviceStateRegistry.setStopRecordingCallback(this.stopVideoRecording);
            }
        }
    }

    public stopVideoRecording = () => {

        if (this.isRecordingRunning) {
            if (this.rcState.recordingWithAudio) {
                RcContext.instance.session.sendRequest(new AgentRecordingStopMessage())
                    .pipe(takeUntil(this.onDestroy))
                    .subscribe((message: AgentRecordingMessage) => {
                        // This method does need to be called. Otherwise the server wont send the vide activity
                        // But we dont need to change the video recording state state here,
                        // as it will be done when the activity gets attached to the ticket
                        RcContext.instance.session.sendCallbackRequest(new AgentRecordingGetMessage(), (response) => { });
                    });
                // Assume stop will succeed and we will proceed to video upload.
                // This will also transition UI to stopped state immediately and
                // give user immediate feedback. If we have failure we will
                // transitioned to stopped later.
                this.transitionToUploading();
            } else {
                RcContext.instance.session.sendMessage(new VideoRecordingStopMessage());
                // because onVideoRecordingStopped is not invoked when
                // browser client is requesting to stop recording, we
                // transition UI to stopped state immediately
                this.transitionToTranscoding();
            }
        }
    };

    public mapAgentRecordingStopReasonMessage(reason: AgentRecordingStopReason) {
        switch (reason) {
            case AgentRecordingStopReason.RequestedByUser:
                return this.saI18nPipe.transform('agentVideoRecording_StopReason_UserRequest');
            case AgentRecordingStopReason.TimeLimit:
                return this.saI18nPipe.transform('agentVideoRecording_StopReason_TimeLimit');
            case AgentRecordingStopReason.SizeLimit:
                return this.saI18nPipe.transform('agentVideoRecording_StopReason_SizeLimit');
            case AgentRecordingStopReason.Unspecified:
            default:
                return this.saI18nPipe.transform('agentVideoRecording_StopReason_Unspecified');
        }
    }

    public videoDownloadReady = (videoActivityId: number, fileInfo: FileInfo): void => {
        const videoNames = this.legacyRcService.getRecordedVideoNames();
        if (videoNames.length > 0) {
            videoNames.reverse();
            const downloadFile = videoNames.pop();
            this.downloadFile(videoActivityId, fileInfo, downloadFile);
        }
    };

    private _recordedTimeStart = null;
    private _recordedTimePauseStart = null;

    private get pausedTime(): number {
        return !this._recordedTimePauseStart ? 0 : Date.now() - this._recordedTimePauseStart;
    }

    // calculate the recorded time including the active pause (as the pause ends, the start time gets updated)
    public get recordedTime() {
        return !this._recordedTimeStart ? 0 : Math.floor(((this._recordedTimePauseStart || Date.now()) - this._recordedTimeStart) / 1000);
    }

    private stopAndResetTimer() {
        this._recordedTimeStart = null;
        this._recordedTimePauseStart = null;
    }

    // Server-side video recording specific event: indicates video transcoding and attachment
    // is is currently in progress
    private transitionToTranscoding = (win32Error?: number) => {
        // This transitions UI to the stopped state already, to give user instant feedback
        this.onStopRecording();
        this.rcState.recordingState = RecordingState.TranscodingVideo;
        this.videoRecordingStatusChanged.postMessage({
            state: RecordingState.TranscodingVideo,
            withAudio: this.rcState.recordingWithAudio,
            win32ErrorCode: win32Error,
        });

        this.stopAndResetTimer();
    };

    // Emits recording state update and updates rcState as well as UI
    private transitionToUploading = (win32Error?: number, recordingWithAudioStopReason?: number) => {
        // This transitions UI to the stopped state already, to give user instant feedback
        this.onStopRecording();
        this.rcState.recordingState = RecordingState.UploadingVideo;
        this.videoRecordingStatusChanged.postMessage({
            state: RecordingState.UploadingVideo,
            withAudio: this.rcState.recordingWithAudio,
            win32ErrorCode: win32Error,
            recordingWithAudioStopReason
        });

        this.stopAndResetTimer();
    };

    // Emits recording state update and updates rcState as well as UI
    private transitionToPaused = () => {
        this.rcState.recordingPaused = true;
        this.rcState.recordingState = RecordingState.Paused;
        this.videoRecordingStatusChanged.postMessage({
            state: RecordingState.Paused,
            withAudio: this.rcState.recordingWithAudio
        });

        this._recordedTimePauseStart = Date.now();
        this.pause.next(true);
    };

    // Emits recording state update and updates rcState as well as UI
    private transitionToStopped = (win32Error?: number, recordingWithAudioStopReason?: number, serverStatus?: SotiAssistStatus) => {
        // Reset paused flag in case video is being stopped from pausing state
        this.onStopRecording();
        if (this.rcState) {
            this.rcState.recordingState = RecordingState.NotRunning;
        }
        this.videoRecordingStatusChanged.postMessage({
            state: RecordingState.NotRunning,
            // When RC closed, before finishing processing the video, we cant check if the recording had audio or not
            // Shouldn't matter anyway as the recorderd finished
            withAudio: this.rcState != null ? this.rcState.recordingWithAudio : false,
            win32ErrorCode: win32Error,
            serverStatusCode: serverStatus,
            recordingWithAudioStopReason
        });
        this.pause.next(false);

        this.stopAndResetTimer();
    };

    // In case of agent/with audio recording, transitions to
    // a state where aknowledgement prompt is shown to the
    // device user and recording awaits for them to be accepted
    private transitionToPendingAccept = () => {
        this.pause.next(false);

        this.rcState.recordingState = RecordingState.PendingAccept;
        this.videoRecordingStatusChanged.postMessage({
            state: RecordingState.PendingAccept,
            withAudio: true
        });

        this.stopAndResetTimer();
    };

    // Emits recording state update and updates rcState as well as UI
    private transitionToRunning = (withAudio: boolean) => {
        this.pause.next(false);

        this.rcState.recordingState = RecordingState.Running;
        this.onStartedRecoring(withAudio);
        this.videoRecordingStatusChanged.postMessage({
            state: RecordingState.Running,
            withAudio: this.rcState.recordingWithAudio
        });
    };

    // Transitions rcState, and thus UI, to stopped recording state
    private onStartedRecoring(withAudio: boolean) {
        this.rcState.recordingWithAudio = withAudio;
        this.rcState.recordingPaused = false;
        this.rcState.recording = true;
        this.rcState.recording$.next(this.rcState.recording);
        // adjust the start time for an ongoing pause, and reset the pause
        this._recordedTimeStart = (this._recordedTimeStart || Date.now()) + this.pausedTime;
        this._recordedTimePauseStart = null;
    }

    // Transitions rcState, and thus UI, to stopped recording state
    private onStopRecording() {
        this.stopAndResetTimer();
        if (this.rcState) {
            this.rcState.recordingPaused = false;
            this.rcState.recording = false;
            this.rcState.recording$.next(this.rcState.recording);
        }
    }

    // Server-status RC events
    private onServerStatusUpdate = () => {

        // Only change the video recording status if the ServerStatusUpdate is related with it
        if ((this.rcState.recording && RcContext.instance.serverStatus && this.isVideoRecordingStatus(RcContext.instance.serverStatus.status))
            || (!this.rcState.recording && SotiAssistStatus.SasVideoMaxDurationReached)) {

            this.transitionToStopped(undefined, undefined, RcContext.instance.serverStatus.status);
            // Server recording expectes stopRecording to be sent to clean-up the encoder state,
            // even when server triggers this stop event.
            RcContext.instance.session.sendMessage(new VideoRecordingStopMessage());
            // Resets the status
            RcContext.instance.serverStatus = {} as { status: SotiAssistStatus; message: string };
        }
    };

    // Gets invoked whenever video recording video processing
    // and attachement to the an activity has been completed
    private onServerVideoRecordingNewVideoAvailable = () => {
        this.transitionToStopped();
    };

    private isVideoRecordingStatus(serverStatus: SotiAssistStatus) {
        return serverStatus === SotiAssistStatus.SasVideoFolderCreationFailed
            || serverStatus === SotiAssistStatus.SasVideoEncodingServerOccupied
            || serverStatus === SotiAssistStatus.SasVideoAttachmentCreationFailed
            || serverStatus === SotiAssistStatus.SasDownloadLinkCreationFailed
            || serverStatus === SotiAssistStatus.SasVideoRecordingEmpty
            || serverStatus === SotiAssistStatus.SasVideoFolderPathTooLong;
    }

    /** Opens the download file dialog */
    private downloadFile = (videoActivityId: number, attachmentInfo: FileInfo, downloadFileName: string): void => {
        const dialogParams = this.attachmentService.createDownloadConfig(downloadFileName);
        dialogParams.placeHolder = this.rcState.device.deviceName;

        const fileSaveParams: hd.FileSaveParams = {
            fileName: dialogParams.placeHolder,
            extension: downloadFileName ? '.' + downloadFileName.split('.').pop() : dialogParams.extension,
        };

        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = true;
        dialogConfig.autoFocus = true;
        dialogConfig.width = '400px';
        dialogConfig.height = '230px';
        dialogConfig.panelClass = 'custom-dialog-scrollable-container';
        dialogConfig.closeOnNavigation = true;

        dialogConfig.data = {
            title: dialogParams.title,
            icon: dialogParams.icon,
            confirmButton: dialogParams.confirmButton,
            dialogParamsTitle: dialogParams.inputTitle,
            extension: dialogParams.extension,
            placeHolder: dialogParams.placeHolder,
            regexFilter: dialogParams.regexFilter,
            unallowedChar: dialogParams.unallowedChar,
            fileParams: fileSaveParams,
        };

        const modalInstance = this.dialog.open(PromptDialogComponent, dialogConfig);
        modalInstance.afterClosed().subscribe((result: FileSaveParams) => {
            if (result) {
                const temp = {
                activityId: videoActivityId,
                activityTypeId: ActivityTypeEnum.attachment,
                details: {
                    fileName: result.fileName,
                    fileId: attachmentInfo.attachmentId,
                },
                } as AttachmentActivity;
                this.attachmentService.createDownloadWithUserInput(temp, result).subscribe(
                (downloadUri: string) => {
                    const fileInfo: IFileInfo = {
                    fileName: result.fileName + fileSaveParams.extension,
                    filePath: downloadUri,
                    };
                    this.attachmentService
                    .downloadFile(fileInfo)
                    .then(() => {
                        console.log(`RC downloaded video file ${fileInfo.fileName}`);
                    })
                    .catch(err => {
                        console.error(err);
                    });
                },
                err => {
                    console.error(err);
                }
                );
            }
        });
        if (this.rcState) {
            this.rcState.recordingState = RecordingState.NotRunning;
        }

        this.legacyRcService.notifyNewVideoAvailable();
    };
}
